import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { decideDeleteRequest, listAccountsSaleDeletions } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { Empty, TH, TR, errMsg, openInGym } from "@/components/accounts/shared";
import { numberLocale } from "@/lib/money";

// Sale deletion requests from the gyms (CR-0038). Only admins delete a sale on All Sales; everyone
// else asks. Since 21 Oct 2026 only an admin approves or declines (Global Admin → Deletion requests has
// every kind); the accounts team sees the sale requests here. Approving deletes the sale (same checks as
// an admin delete); declining needs a note.
export default function SaleDeletions({ gym, country, gyms }) {
  const { user } = useAuth();
  const canDecide = atLeast(user?.role, "admin");
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState("requested");
  const load = useCallback(async () => {
    try { setRows(await listAccountsSaleDeletions()); } catch (e) { toast.error(errMsg(e, "Could not load")); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const countryOf = Object.fromEntries((gyms || []).map((g) => [g.id, g.country]));

  const decide = async (r, accept) => {
    const note = accept ? "" : window.prompt(`Decline deleting ${r.member}'s sale? Say why (the gym sees this).`);
    if (!accept && !note) return;
    if (accept && !window.confirm(`Delete ${r.member}'s sale (${r.sale_date}) at ${r.gym}? This can't be undone.`)) return;
    try {
      await decideDeleteRequest(r.id, { approve: accept, note: note || "" });
      toast.success(accept ? "Sale deleted" : "Declined");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };

  if (!rows) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const shown = rows.filter((r) => (!gym || r.facility_id === gym) && (!country || countryOf[r.facility_id] === country)
    && (show === "all" || r.status === show));
  const waiting = rows.filter((r) => r.status === "requested").length;
  return (
    <div className="card-surface overflow-x-auto" data-testid="accounts-sale-deletions">
      <div className="px-4 py-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100">Sale deletion requests{waiting ? ` · ${waiting} waiting` : ""}</div>
          <div className="text-[11px] text-zinc-500">Only admins can delete a sale on All Sales. Gyms ask (e.g. a sale entered twice) and an admin approves or declines{canDecide ? "" : " (Global Admin → Deletion requests)"}. Approving deletes the sale; it's logged in the audit trail.</div>
        </div>
        <select value={show} onChange={(e) => setShow(e.target.value)} className="bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-xs text-zinc-200">
          <option value="requested">Waiting</option>
          <option value="approved">Approved</option>
          <option value="declined">Declined</option>
          <option value="all">All</option>
        </select>
      </div>
      {shown.length === 0 ? <Empty>No requests here.</Empty> : (
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">Requested</th><th className="px-3 py-2 font-medium">Gym</th>
            <th className="px-3 py-2 font-medium">Sale</th><th className="px-3 py-2 font-medium text-right">Paid</th>
            <th className="px-3 py-2 font-medium">Why</th><th className="px-3 py-2 font-medium">Status</th><th />
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className={TR} data-testid={`sale-deletion-${r.id}`}>
                <td className="px-3 py-2 text-xs text-zinc-400">{String(r.requested_at).slice(0, 10)}<div className="text-[10px] text-zinc-500">{r.requested_by}</div></td>
                <td className="px-3 py-2 text-zinc-300">{r.gym}</td>
                <td className="px-3 py-2 text-xs">
                  <button type="button" className="text-zinc-200 hover:text-blue-300 text-left"
                    onClick={() => openInGym({ facility_id: r.facility_id, path: "/sales-log", q: r.member || "" })}>{r.member}</button>
                  <div className="text-zinc-500">{r.sale_date} · {r.programme || "—"}{r.status_on_sale ? ` · ${r.status_on_sale}` : ""}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-zinc-300">{r.paid != null ? Number(r.paid).toLocaleString(numberLocale()) : "—"}</td>
                <td className="px-3 py-2 text-xs text-zinc-300 max-w-[18rem]">{r.reason}</td>
                <td className="px-3 py-2 text-xs text-zinc-400">{r.status === "requested" ? (canDecide ? "Waiting" : "Waiting for an admin") : r.status}
                  {r.decided_by && <div className="text-[10px] text-zinc-500">{r.decided_by}{r.decision_note ? `: ${r.decision_note}` : ""}</div>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === "requested" && canDecide && <>
                    <Button size="sm" variant="ghost" onClick={() => decide(r, true)} className="h-7 text-emerald-300" data-testid={`accept-deletion-${r.id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(r, false)} className="h-7 text-rose-300" data-testid={`decline-deletion-${r.id}`}><X className="h-3.5 w-3.5 mr-1" />Decline</Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
